import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetchWithRecovery } from '../utils/sessionRecovery';
import { CardsSkeleton, PageHeader } from '../components/admin/AdminUI';
import { PlatformBilling } from './PlatformBilling';

interface SubscriptionInfo {
  workspaceId?: string;
  planCode: string;
  planName: string;
  status: 'active' | 'trialing' | 'past_due' | 'canceled';
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  // BIL-10: these are sent by GET /api/subscription/me (getSubscription) —
  // before, the page read isTrial / trialEndsAt / trialDaysRemaining /
  // trialCreditsRemaining although none of them existed in the payload.
  isTrial: boolean;
  trialEndsAt?: string | null;
  trialDaysRemaining?: number | null;
  trialExpired?: boolean;
}

interface CreditBalance {
  available: number;
  reserved: number;
  total: number;
}

interface TrialInfo {
  trialEndsAt: string | null;
  trialDaysRemaining: number | null;
  trialExpired: boolean;
}

/**
 * Billing page - shows subscription status, credits, and upgrade options
 */
export function BillingPage() {
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [credits, setCredits] = useState<CreditBalance | null>(null);
  const [trial, setTrial] = useState<TrialInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // A 403 only. It used to be set for 401 as well, and the 401 case rendered
  // "Your session has expired. Sign in again…" with nothing to press — a dead
  // end that named the fix and withheld it. A dead session is now recovered
  // automatically by `authFetchWithRecovery` and never reaches the render tree.
  // A 403 is a real answer about access and still deserves a screen.
  const [accessDenied, setAccessDenied] = useState(false);
  const [isManaging, setIsManaging] = useState(false);
  // BIL-10: "Manage" opens the Stripe portal, which needs a Stripe customer.
  // Gated on that fact (GET /api/billing/provider), not on status === 'active'.
  const [hasStripeCustomer, setHasStripeCustomer] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    // These endpoints are behind header-only `jwtAuth`. This page used to call
    // them with `credentials: 'include'` and no Authorization header, so both
    // always returned 401 — and the page rendered that as "No subscription
    // found" and "Unable to load credit balance". A workspace with a live paid
    // subscription was told it had none.
    // Set when the session turned out to be dead and the browser is on its way
    // to Central sign-in. It exists to keep `finally` from clearing the loading
    // state underneath us: an early `return` inside `try` still runs `finally`,
    // so without this the spinner was replaced by a fully-rendered empty page —
    // "No subscription found" / "Unable to load credit balance" — for however
    // long the navigation took. A flash of confidently wrong content is the same
    // defect class as the interstitial this change removes, just briefer.
    let navigatingAway = false;
    try {
      const [sub, creditsResult] = await Promise.all([
        authFetchWithRecovery('/api/subscription/me'),
        authFetchWithRecovery('/api/credits/balance'),
      ]);

      // A null response means the session was dead and recovery has taken over.
      // Keep the spinner and stop: this document is going away.
      if (sub.response === null || creditsResult.response === null) {
        navigatingAway = true;
        return;
      }

      const subRes = sub.response;
      const creditsRes = creditsResult.response;

      // An auth failure is not an absence of data, and must never be rendered
      // as one: "you have no subscription" is a statement about the account,
      // and making it on the strength of a 403 is simply false.
      if (subRes.status === 403) {
        setAccessDenied(true);
        setIsLoading(false);
        return;
      }

      if (subRes.ok) {
        const subData = await subRes.json();
        setSubscription({ ...subData, isTrial: subData.isTrial === true || subData.status === 'trialing' });

        // Extract trial info if in trial
        if (subData.isTrial || subData.status === 'trialing') {
          setTrial({
            trialEndsAt: subData.trialEndsAt || subData.currentPeriodEnd || null,
            trialDaysRemaining: typeof subData.trialDaysRemaining === 'number' ? subData.trialDaysRemaining : null,
            trialExpired: subData.trialExpired === true,
          });
        }

        // Best effort: a failure here only hides "Manage Subscription".
        try {
          const provider = await authFetchWithRecovery('/api/billing/provider');
          if (provider.response?.ok) {
            const providerData = await provider.response.json();
            setHasStripeCustomer(providerData.customerIdPresent === true);
          }
        } catch {
          // leave Manage hidden
        }
      }

      if (creditsRes.status === 403) {
        setAccessDenied(true);
      } else if (creditsRes.ok) {
        const creditsData = await creditsRes.json();
        // A zero balance is a real answer. Rendering it as "unable to load"
        // would report a working API as broken.
        setCredits(creditsData);
      }
    } catch (err) {
      setError('Failed to load billing information');
    } finally {
      if (!navigatingAway) setIsLoading(false);
    }
  };

  // BIL-06: "Upgrade" used to POST the legacy /api/billing/stripe/checkout
  // with NO body — which bills the CURRENT plan's modules and cannot buy a
  // different plan. Upgrades now go through the Central plans page, which
  // picks the plan and uses /api/billing/plan/checkout or /plan/change.
  const handleUpgrade = () => {
    const workspaceId = subscription?.workspaceId;
    if (!workspaceId) {
      setError('Could not determine the workspace for this subscription.');
      return;
    }
    navigate(`/workspace/${encodeURIComponent(workspaceId)}/settings/billing?view=plans`);
  };

  const handleManageSubscription = async () => {
    setIsManaging(true);
    try {
      const { response } = await authFetchWithRecovery('/api/billing/stripe/portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response === null) return;

      const data = await response.json();

      if (response.ok && data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Failed to open billing portal');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsManaging(false);
    }
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string, isTrial: boolean) => {
    if (isTrial) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
          Trial
        </span>
      );
    }

    const styles: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      past_due: 'bg-yellow-100 text-yellow-800',
      canceled: 'bg-red-100 text-red-800',
      trialing: 'bg-blue-100 text-blue-800',
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || 'bg-slate-100 text-slate-800'}`}>
        {status.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
      </span>
    );
  };

  if (isLoading) {
    return (
      <CardsSkeleton message="Loading billing…" cards={2} />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader as="h1" title="Billing" subtitle="Billing across every workspace, then your own workspace's plan and credits" />

      <section aria-labelledby="platform-billing-heading" className="space-y-4">
        <h2 id="platform-billing-heading" className="text-lg font-semibold text-slate-900">
          All workspaces
        </h2>
        <PlatformBilling />
      </section>

      <h2 className="pt-2 text-lg font-semibold text-slate-900">Your workspace</h2>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Trial Banner — only facts the API actually sends (BIL-10). */}
      {trial && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-blue-900">
                {trial.trialExpired
                  ? 'Your trial has ended'
                  : trial.trialDaysRemaining !== null && trial.trialDaysRemaining > 0
                    ? `${trial.trialDaysRemaining} ${trial.trialDaysRemaining === 1 ? 'day' : 'days'} left in your trial`
                    : 'Your trial ends today'}
              </h3>
              {trial.trialEndsAt && (
                <p className="text-sm text-blue-700 mt-1">
                  {trial.trialExpired ? 'Ended' : 'Ends'} {formatDate(trial.trialEndsAt)}
                </p>
              )}
            </div>
            <button
              onClick={handleUpgrade}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700"
            >
              {trial.trialExpired ? 'Choose a plan' : 'Upgrade Now'}
            </button>
          </div>
        </div>
      )}

      {/* Subscription Card */}
      <div className="bg-white rounded-xl shadow-sm ring-1 ring-slate-200/80 p-6">
        <h2 className="text-lg font-medium text-slate-900 mb-4">Current Plan</h2>

        {subscription ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xl font-semibold text-slate-900 capitalize">
                  {subscription.planName || subscription.planCode}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  {subscription.currentPeriodEnd && !subscription.isTrial && (
                    <>Next billing: {formatDate(subscription.currentPeriodEnd)}</>
                  )}
                  {subscription.isTrial && trial?.trialEndsAt && (
                    <>Trial ends: {formatDate(trial.trialEndsAt)}</>
                  )}
                </p>
              </div>
              {getStatusBadge(subscription.status, subscription.isTrial)}
            </div>

            {subscription.cancelAtPeriodEnd && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  Your subscription will be canceled at the end of the current period.
                </p>
              </div>
            )}

            <div className="flex gap-3">
              {hasStripeCustomer && (
                <button
                  onClick={handleManageSubscription}
                  disabled={isManaging}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-md font-medium hover:bg-slate-50 disabled:opacity-50"
                >
                  {isManaging ? 'Loading...' : 'Manage Subscription'}
                </button>
              )}
              {(subscription.status === 'trialing' || subscription.isTrial || subscription.status === 'canceled') && (
                <button
                  onClick={handleUpgrade}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700"
                >
                  {subscription.status === 'canceled' || subscription.trialExpired ? 'Choose a plan' : 'Upgrade to Paid'}
                </button>
              )}
            </div>
          </div>
        ) : accessDenied ? (
          <p className="text-slate-500">
            You do not have access to billing for this workspace.
          </p>
        ) : (
          <p className="text-slate-500">No subscription found</p>
        )}
      </div>

      {/* Credits Card */}
      <div className="bg-white rounded-xl shadow-sm ring-1 ring-slate-200/80 p-6">
        <h2 className="text-lg font-medium text-slate-900 mb-4">Credit Balance</h2>

        {credits ? (
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-2xl font-semibold text-slate-900">{credits.available}</p>
              <p className="text-sm text-slate-500">Available</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-2xl font-semibold text-slate-900">{credits.reserved}</p>
              <p className="text-sm text-slate-500">Reserved</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-2xl font-semibold text-slate-900">{credits.total}</p>
              <p className="text-sm text-slate-500">Total</p>
            </div>
          </div>
        ) : accessDenied ? (
          <p className="text-slate-500">
            You do not have access to credits for this workspace.
          </p>
        ) : (
          <p className="text-slate-500">Unable to load credit balance</p>
        )}
      </div>
    </div>
  );
}
